"use client";
import { useState, useEffect } from "react";
import { Box, Textarea, Text, VStack, IconButton } from "@chakra-ui/react";
import { FaComments, FaTimes, FaPaperPlane, FaPaperclip } from "react-icons/fa";
import { useTheme } from "next-themes";
const apiUrl = process.env.NEXT_PUBLIC_API_URL;

/**
 * The ChatWidget component provides a chat interface for users to interact with a chatbot.
 * It includes functionality for sending messages, displaying chat history, and attaching files.
 */
export default function ChatWidget() {
    const [mounted, setMounted] = useState(false);

    /**
     * Sets the mounted state to true after the component is mounted.
     */
    useEffect(() => {
        setMounted(true);
    }, []);

    const { theme } = useTheme();
    const isDark = theme === "dark";

    const chatContainerBg = isDark ? "gray.950" : "white";
    const chatMessagesBg  = isDark ? "gray.800" : "gray.100";
    const botMessageBg    = isDark ? "gray.700" : "gray.200";
    const botMessageColor = isDark ? "gray.50"  : "gray.900";
    const textAreaBg      = isDark ? "gray.800" : "gray.50";
    const textAreaColor   = isDark ? "gray.50"  : "gray.900";
    const chatTitleColor  = isDark ? "blue.300" : "blue.900";

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { text: "Hallo! Wie kann ich helfen?", sender: "bot" }
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState<File | null>(null);

    const getCurrentPageContext = () => {
        if (typeof document === "undefined") return "";

        const title = document.title || "Unbenannter Bereich";
        const pathname = window.location.pathname || "";
        const text = document.body?.innerText || "";
        const cleanText = text
            .replace(/\s+/g, " ")
            .trim()
            .slice(0, 4000);

        return ` Ich sehe aktuell folgende Page: Page title: ${title}\nPage path: ${pathname}\n\nPage content:\n${cleanText}`;
    };

    /**
     * Toggles the chat widget open or closed.
     */
    const toggleChat = () => setIsOpen(!isOpen);

    /**
     * Sends a message to the chatbot and updates the chat history.
     */
    const sendMessage = async () => {
        if (!input.trim()) return;

        const newMessages = [...messages, { text: input, sender: "user" }];
        setMessages(newMessages);
        setInput("");
        setLoading(true);

        try {
            const response = await fetch(`${apiUrl}/chatbot/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    message: input + getCurrentPageContext()
                })
            });

            if (!response.body) {
                throw new Error("Leere Antwort vom Server");
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let botReply = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true }).trim();
                const lines = chunk.split("\n");

                for (const line of lines) {
                    if (line.startsWith("{")) {
                        try {
                            const json = JSON.parse(line);
                            if (json.choices && json.choices[0].delta.content) {
                                let textChunk = json.choices[0].delta.content;
                                textChunk = textChunk.replace("<|eot_id|>", ""); // <|eot_id|> entfernen

                                botReply += textChunk;

                                setMessages((prevMessages) => {
                                    const updatedMessages = [...prevMessages];
                                    if (updatedMessages[updatedMessages.length - 1].sender === "bot") {
                                        updatedMessages[updatedMessages.length - 1].text = botReply;
                                    } else {
                                        updatedMessages.push({ text: botReply, sender: "bot" });
                                    }
                                    return updatedMessages;
                                });
                            }
                        } catch (e) {
                            console.error("Fehler beim Parsen des Streams:", e);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Fehler beim Abrufen der Antwort:", error);
            setMessages([...newMessages, { text: "Fehler beim Abrufen der Antwort!", sender: "bot" }]);
        }

        setLoading(false);
    };

    /**
     * Handles the Enter key press event to send a message.
     * @param {React.KeyboardEvent<HTMLTextAreaElement>} e - The keyboard event.
     */
    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    if (!mounted) return null;

    return (
        <Box position="fixed" bottom={4} right={4}>
            {!isOpen && (
                <IconButton
                    icon={<FaComments />}
                    aria-label="Open Chat"
                    onClick={toggleChat}
                    colorScheme="blue"
                    size="lg"
                    rounded="full"
                />
            )}
            {isOpen && (
                <Box
                    w="500px"
                    bg={chatContainerBg}
                    p={4}
                    boxShadow="xl"
                    borderRadius="lg"
                >
                    <VStack align="stretch" spacing={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Text fontWeight="bold" color={chatTitleColor}>
                                Anno-Bot
                            </Text>
                            <IconButton
                                icon={<FaTimes />}
                                aria-label="Close Chat"
                                onClick={toggleChat}
                                size="sm"
                            />
                        </Box>
                        <Box
                            h="350px"
                            overflowY="auto"
                            p={2}
                            bg={chatMessagesBg}
                            borderRadius="md"
                        >
                            {messages.map((msg, index) => (
                                <Text
                                    key={index}
                                    alignSelf={msg.sender === "user" ? "flex-end" : "flex-start"}
                                    bg={msg.sender === "user" ? "blue.500" : botMessageBg}
                                    color={msg.sender === "user" ? "white" : botMessageColor}
                                    p={2}
                                    borderRadius="md"
                                    mb={1}
                                >
                                    {msg.text}
                                </Text>
                            ))}
                        </Box>
                        <Box display="flex" alignItems="center">
                            <IconButton
                                icon={<FaPaperclip />}
                                aria-label="Attach File"
                                onClick={() => document.getElementById("fileInput")?.click()}
                                colorScheme="gray"
                                mr={2}
                            />
                            <input
                                id="fileInput"
                                type="file"
                                style={{ display: "none" }}
                                onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
                            />
                            <Textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Schreibe eine Nachricht..."
                                isDisabled={loading}
                                resize="vertical"
                                bg={textAreaBg}
                                color={textAreaColor}
                            />
                            <IconButton
                                icon={<FaPaperPlane />}
                                aria-label="Send Message"
                                onClick={sendMessage}
                                colorScheme="blue"
                                ml={2}
                                isLoading={loading}
                            />
                        </Box>
                    </VStack>
                </Box>
            )}
        </Box>
    );
}